import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import clinicService from '../../../../../services/clinic.service';
import Loading from '../../../../common/Loading';
import MediaListItem from './MediaListItem';

const MediaTab = () => {
    const {clinicId} = useParams();
    const [mediaList, setMediaList] = useState([]);
    const [filteredMedia, setFilteredMedia] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOption, setSortOption] = useState('createdAt_desc');
    const itemsPerPage = 6;
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        updateMediaList();
    }, []);

    useEffect(() => {
        // Apply filtering and sorting whenever mediaList, searchTerm, or sortOption changes
        filterAndSortMedia();
    }, [mediaList, searchTerm, sortOption]);

    const updateMediaList = () => {
        setLoading(true);
        clinicService.getMedia(clinicId)
            .then(res => {
                setLoading(false);
                setMediaList(res.data || []); // Ensure it's always an array
            })
            .catch(err => {
                setLoading(false);
                console.error("Error fetching media:", err);
            });
    };

    const filterAndSortMedia = () => {
        let result = [...mediaList];

        // Apply search filter
        if (searchTerm) {
            result = result.filter(item =>
                item.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply sorting
        switch (sortOption) {
            case 'name_asc':
                result.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case 'name_desc':
                result.sort((a, b) => b.name.localeCompare(a.name));
                break;
            case 'createdAt_asc':
                result.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                break;
            case 'createdAt_desc':
                result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                break;
            default:
                break;
        }

        setFilteredMedia(result);
        setCurrentPage(1); // Reset to first page when filters/sort change
    };

    const handleUpload = (file) => {
        if (!file) return;

        setIsUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('file', file);

        clinicService.uploadMedia(clinicId, formData, (progressEvent) => {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setUploadProgress(progress);
        })
            .then(() => {
                updateMediaList();
            })
            .catch(err => {
                console.error("Upload error:", err);
            })
            .finally(() => {
                setIsUploading(false);
                setUploadProgress(0);
            });
    };

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredMedia.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredMedia.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <div className="card-padding shadow-none">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="card-title mb-0">
                    <i className="bi bi-images me-2"></i> Clinic Media
                </div>
            </div>

            <div className="card-padding shadow-none">
                <div className="card-body">
                    {/* Upload Section */}
                    <div className="mb-4 p-3 border rounded bg-light">
                        <h6 className="mb-3">Upload New Media</h6>
                        <div
                            className={`border rounded p-4 text-center ${isUploading ? 'opacity-50' : ''}`}
                            style={{
                                borderStyle: 'dashed',
                                cursor: 'pointer',
                                backgroundColor: isDragging ? 'rgba(0, 123, 255, 0.1)' : 'inherit'
                            }}
                            onClick={() => document.getElementById('mediaUpload').click()}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setIsDragging(true);
                            }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setIsDragging(false);
                                if (e.dataTransfer.files.length > 0) {
                                    handleUpload(e.dataTransfer.files[0]);
                                }
                            }}
                        >
                            <input
                                type="file"
                                className="d-none"
                                id="mediaUpload"
                                onChange={(e) => handleUpload(e.target.files[0])}
                                disabled={isUploading}
                            />

                            <div className="d-flex flex-column align-items-center justify-content-center">
                                <i className="bi bi-cloud-arrow-up fs-1 text-primary mb-2"></i>
                                <h5 className="mb-1">
                                    {isDragging ? 'Drop files here' : 'Drag & drop files or click to browse'}
                                </h5>
                                <small className="text-muted">
                                    Supported formats: JPG, PNG, GIF, PDF etc.
                                </small>
                            </div>

                            {isUploading && (
                                <div className="mt-3">
                                    <div className="progress" style={{ height: "6px" }}>
                                        <div
                                            className="progress-bar progress-bar-striped progress-bar-animated"
                                            role="progressbar"
                                            style={{ width: `${uploadProgress}%` }}
                                            aria-valuenow={uploadProgress}
                                            aria-valuemin="0"
                                            aria-valuemax="100"
                                        ></div>
                                    </div>
                                    <small className="text-muted d-block mt-1">
                                        Uploading: {uploadProgress}%
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Search and Sort Controls */}
                    <div className="card-padding shadow-none">
                        <div className="row">
                            <div className="col-md-8 mb-2 mb-md-0">
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i className="bi bi-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search media by name..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <button
                                            className="input-group-text bg-danger text-white border-start-0"
                                            onClick={() => setSearchTerm('')}
                                            style={{
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)'}
                                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <i className="bi bi-x"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i className="bi bi-sort-down"></i>
                                    </span>
                                    <select
                                        className="form-select"
                                        value={sortOption}
                                        onChange={(e) => setSortOption(e.target.value)}
                                    >
                                        <option value="createdAt_desc" selected>Newest First</option>
                                        <option value="createdAt_asc">Oldest First</option>
                                        <option value="name_asc">Name (A-Z)</option>
                                        <option value="name_desc">Name (Z-A)</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Media List */}
                    {loading ? (
                        <div className="text-center py-5">
                            <Loading/>
                        </div>
                    ) : (
                        <>
                            {filteredMedia.length > 0 ? (
                                <>
                                    <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 g-4">
                                        {currentItems.map((item) => (
                                            <div className="col" key={item.id}>
                                                <MediaListItem
                                                    item={item}
                                                    updateMediaList={updateMediaList}
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <nav className="mt-4">
                                            <ul className="pagination justify-content-center">
                                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => paginate(currentPage - 1)}
                                                    >
                                                        &laquo; Previous
                                                    </button>
                                                </li>

                                                {[...Array(totalPages).keys()].map(number => (
                                                    <li
                                                        key={number + 1}
                                                        className={`page-item ${currentPage === number + 1 ? 'active' : ''}`}
                                                    >
                                                        <button
                                                            className="page-link"
                                                            onClick={() => paginate(number + 1)}
                                                        >
                                                            {number + 1}
                                                        </button>
                                                    </li>
                                                ))}

                                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => paginate(currentPage + 1)}
                                                    >
                                                        Next &raquo;
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    )}
                                </>
                            ) : (
                                <div className="text-center py-5">
                                    <div className="alert alert-warning" role="alert">
                                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                        {searchTerm ? 'No matching media found' : 'No media found'}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MediaTab;